import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import styles from './popover.scss';
import Show from '../../hoc/show';
import { POSITION } from '../../constants/positioning';
import { screenContext } from '../../services/screen';

const classNames = require('classnames');

const classNameByPosition = {
  [POSITION.TOP]: styles.popoverContainerTop,
  [POSITION.BOTTOM]: styles.popoverContainerBottom,
  [POSITION.LEFT]: styles.popoverContainerLeft,
};
const classNameByAlignment = {
  [POSITION.RIGHT]: styles.alignRight,
  [POSITION.LEFT]: styles.alignLeft,
};

const KEY_CODES = {
  arrowDown: 40,
  arrowUp: 38,
  esc: 27,
  tab: 9,
};

export class Popover extends React.Component {
  popoverRef = null;
  triggerButtonRef = null;

  constructor(props) {
    super(props);
    this.state = {
      isVisible: false,
      position: props.position || POSITION.BOTTOM,
      marginTop: 0,
    };
  }

  setPopoverRef = (ref) => (this.popoverRef = ref);

  setTriggerButtonRef = (ref) => {
    this.triggerButtonRef = ref;
  };

  focusTriggerButton = () => this.triggerButtonRef && this.triggerButtonRef.focus();

  focusFirstMenuItem = () => this.getAllMenuItems()[0].focus();

  setFocusAfterToggle = (isOpen) => {
    setTimeout(() => {
      isOpen ? this.focusFirstMenuItem() : this.focusTriggerButton();
    }, 0);
  };

  getAllMenuItems = () => {
    const allItemNodes = document.getElementById('menu').querySelectorAll("[role='menuitem']");
    return Array.prototype.slice.call(allItemNodes);
  };

  moveNext = (currentElem) => {
    const allMenuItems = this.getAllMenuItems();
    const indexOfCurrentItem = allMenuItems.indexOf(currentElem);
    const newIndex = (indexOfCurrentItem + 1) % allMenuItems.length;
    allMenuItems[newIndex].focus();
  };

  movePrev = (currentElem) => {
    const allMenuItems = this.getAllMenuItems();
    const indexOfCurrentItem = allMenuItems.indexOf(currentElem);
    const newIndex = (indexOfCurrentItem + allMenuItems.length - 1) % allMenuItems.length;
    allMenuItems[newIndex].focus();
  };

  operateMenu = (event) => {
    const keyCode = event.keyCode;

    if (keyCode === KEY_CODES.arrowDown) {
      event.preventDefault();
      this.moveNext(document.activeElement);
    }

    if (keyCode === KEY_CODES.arrowUp) {
      event.preventDefault();
      this.movePrev(document.activeElement);
    }

    if (keyCode === KEY_CODES.esc || keyCode === KEY_CODES.tab) {
      event.preventDefault();
      this.handlePopoverToggle(event);
    }
  };

  toggleListeners = (isVisible) => {
    const domObject = window.document;

    if (isVisible) {
      domObject.addEventListener('click', this.handlePopoverToggle);
      domObject.addEventListener('keydown', this.operateMenu);
      return;
    }
    domObject.removeEventListener('click', this.handlePopoverToggle);
    domObject.removeEventListener('keydown', this.operateMenu);
  };

  widgetBlurListener = (isVisible) =>
    isVisible
      ? window.addEventListener('blur', this.handlePopoverToggle)
      : window.removeEventListener('blur', this.handlePopoverToggle);

  adjustPosition = (clientY) => {
    const popoverHeight = this.popoverRef ? this.popoverRef.offsetHeight : 0;
    if (this.props.dynamicPositioning) {
      const context = screenContext(clientY, popoverHeight, this.props.shouldUsePopoverDynamicPositioning);
      this.setState({
        position: context.fit ? POSITION.BOTTOM : POSITION.TOP,
        marginTop: context.top,
      });
    }
  };

  getClientY = (event, isVisible) => {
    if (this.props.shouldUsePopoverDynamicPositioning) {
      return isVisible ? null : event.currentTarget?.getBoundingClientRect()?.y;
    }

    return event.clientY;
  };

  handlePopoverToggle = (event) => {
    event.preventDefault();

    const { isVisible } = this.state;
    const { onClose, onOpen } = this.props;
    const clientY = this.getClientY(event, isVisible);

    isVisible ? onClose() : onOpen();

    if (!document) {
      return;
    }

    this.setFocusAfterToggle(!isVisible);
    this.toggleListeners(!isVisible);
    this.widgetBlurListener(!isVisible);

    this.setState({ isVisible: !isVisible }, () => this.adjustPosition(clientY));
  };

  renderPopover = () => {
    const { content, alignment } = this.props;
    const { position, marginTop } = this.state;

    const containerClasses = [
      styles.popoverContainer,
      'ma-popover-bg',
      'ma-popover-border',
      'ma-popover-link',
      classNameByPosition[position],
      classNameByAlignment[alignment],
    ];

    return (
      <div className={classNames(...containerClasses)} ref={this.setPopoverRef} style={{ marginTop }}>
        <div id="menu" aria-live="polite" role="menu" className={classNames(styles.popover, 'ma-popover-link')}>
          {content}
        </div>
      </div>
    );
  };

  render = () => {
    const { className, children, dataHook } = this.props;
    return (
      <button
        type="button"
        data-hook={dataHook}
        id="trigger"
        aria-pressed={this.state.isVisible}
        aria-haspopup="menu"
        aria-label={this.props.ariaLabel}
        className={classNames(className, styles.popoverBtn)}
        onClick={this.handlePopoverToggle}
        onFocus={this.props.onFocus}
        onBlur={this.props.onBlur}
        ref={this.setTriggerButtonRef}
      >
        {children}
        <Show when={this.state.isVisible}>{this.renderPopover()}</Show>
      </button>
    );
  };
}

Popover.propTypes = {
  content: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  dataHook: PropTypes.string,
  position: PropTypes.oneOf(_.values(POSITION)),
  dynamicPositioning: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  alignment: PropTypes.oneOf(_.values(POSITION)),
  ariaLabel: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

Popover.defaultProps = {
  dynamicPositioning: true,
  onClose: () => {},
  onOpen: () => {},
  onFocus: () => {},
  onBlur: () => {},
  alignment: POSITION.RIGHT,
};

export default Popover;
